import api from './api';
import type {
  ArchivesData,
  ArchivedBankAccount,
  ArchivedPersonalOperation,
  ArchivedSharedOperation,
  ArchivedSharedBankAccount,
  ArchivedBudget,
  ArchivedEnvelope,
  ArchivedCategory,
  ArchivedPartner2IncomeChange,
  ArchiveStats,
  RestoreResult,
} from '../types';

/**
 * Récupère toutes les archives de l'utilisateur
 */
export async function getAllArchives(): Promise<ArchivesData> {
  const response = await api.get('/archives');
  return response.data.data;
}

/**
 * Récupère les statistiques d'archives
 */
export async function getArchiveStats(): Promise<ArchiveStats> {
  const response = await api.get('/archives/stats');
  return response.data.data;
}

/**
 * Récupère les comptes bancaires archivés
 */
export async function getArchivedBankAccounts(): Promise<ArchivedBankAccount[]> {
  const response = await api.get('/archives/bank-accounts');
  return response.data.data;
}

/**
 * Récupère les opérations personnelles archivées
 */
export async function getArchivedPersonalOperations(
  includeWithAccount = false
): Promise<ArchivedPersonalOperation[]> {
  const response = await api.get('/archives/personal-operations', {
    params: { includeWithAccount },
  });
  return response.data.data;
}

/**
 * Récupère les opérations partagées archivées
 */
export async function getArchivedSharedOperations(): Promise<ArchivedSharedOperation[]> {
  const response = await api.get('/archives/shared-operations');
  return response.data.data;
}

/**
 * Récupère les budgets archivés
 */
export async function getArchivedBudgets(): Promise<ArchivedBudget[]> {
  const response = await api.get('/archives/budgets');
  return response.data.data;
}

/**
 * Restaure un compte bancaire archivé
 */
export async function restoreBankAccount(archiveId: string): Promise<RestoreResult> {
  const response = await api.post(`/archives/bank-accounts/${archiveId}/restore`);
  return response.data.data;
}

/**
 * Restaure une opération personnelle archivée
 */
export async function restorePersonalOperation(archiveId: string): Promise<RestoreResult> {
  const response = await api.post(`/archives/personal-operations/${archiveId}/restore`);
  return response.data.data;
}

/**
 * Restaure une opération partagée archivée
 */
export async function restoreSharedOperation(archiveId: string): Promise<RestoreResult> {
  const response = await api.post(`/archives/shared-operations/${archiveId}/restore`);
  return response.data.data;
}

/**
 * Restaure un budget archivé
 */
export async function restoreBudget(archiveId: string): Promise<RestoreResult> {
  const response = await api.post(`/archives/budgets/${archiveId}/restore`);
  return response.data.data;
}

/**
 * Supprime définitivement un compte bancaire archivé
 */
export async function permanentDeleteBankAccount(archiveId: string): Promise<void> {
  await api.delete(`/archives/bank-accounts/${archiveId}`);
}

/**
 * Supprime définitivement une opération personnelle archivée
 */
export async function permanentDeletePersonalOperation(archiveId: string): Promise<void> {
  await api.delete(`/archives/personal-operations/${archiveId}`);
}

/**
 * Supprime définitivement une opération partagée archivée
 */
export async function permanentDeleteSharedOperation(archiveId: string): Promise<void> {
  await api.delete(`/archives/shared-operations/${archiveId}`);
}

/**
 * Récupère les comptes partagés archivés
 */
export async function getArchivedSharedBankAccounts(): Promise<ArchivedSharedBankAccount[]> {
  const response = await api.get('/archives/shared-bank-accounts');
  return response.data.data;
}

/**
 * Restaure un compte partagé archivé
 */
export async function restoreSharedBankAccount(archiveId: string): Promise<RestoreResult> {
  const response = await api.post(`/archives/shared-bank-accounts/${archiveId}/restore`);
  return response.data.data;
}

/**
 * Supprime définitivement un compte partagé archivé
 */
export async function permanentDeleteSharedBankAccount(archiveId: string): Promise<void> {
  await api.delete(`/archives/shared-bank-accounts/${archiveId}`);
}

/**
 * Supprime définitivement un budget archivé
 */
export async function permanentDeleteBudget(archiveId: string): Promise<void> {
  await api.delete(`/archives/budgets/${archiveId}`);
}

/**
 * Récupère les enveloppes archivées
 */
export async function getArchivedEnvelopes(): Promise<ArchivedEnvelope[]> {
  const response = await api.get('/archives/envelopes');
  return response.data.data;
}

/**
 * Restaure une enveloppe archivée
 */
export async function restoreEnvelope(archiveId: string): Promise<RestoreResult> {
  const response = await api.post(`/archives/envelopes/${archiveId}/restore`);
  return response.data.data;
}

/**
 * Supprime définitivement une enveloppe archivée
 */
export async function permanentDeleteEnvelope(archiveId: string): Promise<void> {
  await api.delete(`/archives/envelopes/${archiveId}`);
}

/**
 * Récupère les catégories archivées
 */
export async function getArchivedCategories(): Promise<ArchivedCategory[]> {
  const response = await api.get('/archives/categories');
  return response.data.data;
}

/**
 * Restaure une catégorie archivée
 */
export async function restoreCategory(archiveId: string): Promise<RestoreResult> {
  const response = await api.post(`/archives/categories/${archiveId}/restore`);
  return response.data.data;
}

/**
 * Supprime définitivement une catégorie archivée
 */
export async function permanentDeleteCategory(archiveId: string): Promise<void> {
  await api.delete(`/archives/categories/${archiveId}`);
}

/**
 * Récupère les changements de revenus du partenaire 2 archivés
 */
export async function getArchivedPartner2Incomes(): Promise<ArchivedPartner2IncomeChange[]> {
  const response = await api.get('/archives/partner2-incomes');
  return response.data.data;
}

/**
 * Restaure un changement de revenu du partenaire 2 archivé
 */
export async function restorePartner2Income(archiveId: string): Promise<RestoreResult> {
  const response = await api.post(`/archives/partner2-incomes/${archiveId}/restore`);
  return response.data.data;
}

/**
 * Supprime définitivement un changement de revenu archivé
 */
export async function permanentDeletePartner2Income(archiveId: string): Promise<void> {
  await api.delete(`/archives/partner2-incomes/${archiveId}`);
}
